import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Alert,
} from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { clearLinkedUserId } from '../../utils/persistentUserId';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ open, onClose }) => {
  const { getToken, logout, userId } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        setError('Unable to authenticate. Please try again.');
        setIsDeleting(false);
        return;
      }

      if (!userId) {
        setError('Unable to identify your account. Please try again.');
        setIsDeleting(false);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(
        `${apiUrl}/api/account?auth0Id=${encodeURIComponent(userId)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      // Treat 401 as success: it means the JWT belongs to an already-deleted account
      // (e.g. a retry, a stale tab, or a double-click), so the user is functionally out.
      if (!response.ok && response.status !== 401) {
        // Only use generic message to avoid leaking backend details
        throw new Error('Failed to delete account. Please try again or contact support.');
      }

      // Clear local state and log out
      clearLinkedUserId();
      logout();
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#1C1C1E', borderRadius: '12px' },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Inter', color: '#F2F2F3', textAlign: 'center' }}>
        Delete Account?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography
            sx={{ fontFamily: 'Inter', color: 'rgba(242,242,243,0.7)', textAlign: 'center' }}
          >
            Are you sure you want to delete your account? You will be signed out and will no longer
            be able to access your account.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          disabled={isDeleting}
          sx={{
            fontFamily: 'Inter',
            textTransform: 'none',
            color: 'rgba(242,242,243,0.85)',
            borderColor: 'rgba(242,242,243,0.2)',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          size="small"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ fontFamily: 'Inter', textTransform: 'none' }}
        >
          {isDeleting ? 'Deleting...' : 'Delete My Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAccountDialog;
